/**
 * Has two other variants - link and group
 */
import { ComponentProps, MouseEvent, MutableRefObject, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

import EditorNavItem, {
  EditorNavItemBase,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem';
import EditorRenameableNavItem, {
  blockDoubleClick,
  EditorRenamebleNavItemProps,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/EditorRenameableNavItem';
import EditorNavItemLink, { editorLinkCSS } from 'dashboard/components/EditorNavItemLink';

export const DEFAULT_OFFSET = 32;

const ScrollLinkContainer = styled('div')<{ isActive: boolean; withHighlight: boolean }>`
  ${editorLinkCSS}
  background: none;
  width: 100%;

  ${(props) =>
    props.isActive &&
    props.withHighlight &&
    css`
      ${EditorNavItemBase} {
        background-color: ${({ theme }) => theme.color.primary.brand100};
      }
    `}
`;

export interface NavItemScrollConfig {
  columnRef: MutableRefObject<HTMLDivElement | null>;
  // content ref, scrolled to onClick
  sectionRef: MutableRefObject<HTMLDivElement | null>;
  offset?: number;
}

export interface EditorNavItemScrollProps
  extends Omit<ComponentProps<typeof EditorNavItemLink>, 'route'> {
  isActive?: boolean;
  scrollConfig?: NavItemScrollConfig;
  openWithGroup?: boolean;
  withHighlight?: boolean;
  withInstantScroll?: boolean;
  setActiveScrollItemId: (scrollItemId: string) => void;
  useRenameable?: boolean;
}

/**
 * editor nav item - scroll variant
 */
const EditorNavItemScroll = ({
  id,
  dataTest,
  className,
  label,
  scrollConfig,
  isActive,
  isVisible,
  disabled,
  openWithGroup,
  withHighlight,
  withInstantScroll,
  prefixIcon,
  suffixIcon,
  setActiveScrollItemId,
  onClick,
  useRenameable,
  form,
  path,
  title,
  displayPlaceholder,
  editDisabled,
}: EditorNavItemScrollProps &
  Partial<
    Pick<
      EditorRenamebleNavItemProps,
      'form' | 'path' | 'displayPlaceholder' | 'title' | 'editDisabled'
    >
  >) => {
  const scrollToSection = useCallback(
    () =>
      scrollConfig?.columnRef?.current?.scrollTo({
        top:
          scrollConfig?.sectionRef?.current?.offsetTop ||
          0 - (scrollConfig.offset ?? DEFAULT_OFFSET),
        behavior: withInstantScroll ? 'auto' : 'smooth',
      }),
    [scrollConfig, withInstantScroll],
  );

  // trigger hook only when active state changes for automatic scroll
  useEffect(() => {
    if (isActive && openWithGroup) {
      scrollToSection();
    }
  }, [isActive]);

  const handleScroll = useCallback(
    (e: MouseEvent) => {
      setActiveScrollItemId(id);
      scrollToSection();
      onClick?.(e);
    },
    [id, onClick, scrollToSection, setActiveScrollItemId],
  );

  const dataTestId = dataTest ?? `editor_navitem__${id}-scroll`;
  const sharedProps = {
    disabled,
    active: isActive,
    visible: isVisible,
    prefixIcon,
    suffixIcon,
  };

  return (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <ScrollLinkContainer
      className={className}
      data-test={dataTestId}
      id={dataTestId}
      withHighlight={withHighlight}
      isActive={isActive}
    >
      {!useRenameable && (
        <EditorNavItem {...sharedProps} onClick={handleScroll}>
          {label}
        </EditorNavItem>
      )}
      {useRenameable && (
        <EditorRenameableNavItem
          {...sharedProps}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onClick={(e) => blockDoubleClick(e, handleScroll)}
          title={title}
          displayPlaceholder={displayPlaceholder}
          editDisabled={editDisabled}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          form={form}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          path={path}
        >
          {label}
        </EditorRenameableNavItem>
      )}
    </ScrollLinkContainer>
  );
};

export default EditorNavItemScroll;
